import { addQueryParams } from '@st/util/url'
import { Option, Schema } from 'effect'
import { makeRouteMatcher } from './route-matcher'

type ParamNames<R extends string = string, O extends string = string> = {
  required: R
  optional: O
}

type WithOptionalParam<Params extends ParamNames, Name extends string> = ParamNames<
  Params['required'],
  Params['optional'] | Name
>
type WithRequiredParam<Params extends ParamNames, Name extends string> = ParamNames<
  Params['required'] | Name,
  Params['optional']
>

type InferPathSegment<
  Segment extends string,
  Params extends ParamNames
> = Segment extends `:${infer Name}?`
  ? WithOptionalParam<Params, Name>
  : Segment extends `:${infer Name}*`
  ? WithOptionalParam<Params, Name>
  : Segment extends `:${infer Name}+`
  ? WithRequiredParam<Params, Name>
  : Segment extends `:${infer Name}`
  ? WithRequiredParam<Params, Name>
  : Params

type InferPathParams<Path extends string> = Path extends `${infer Head}/${infer Tail}`
  ? InferPathSegment<Head, InferPathParams<Tail>>
  : InferPathSegment<Path, { required: never; optional: never }>

type AllPathParamNames<Path extends string> =
  | InferPathParams<Path>['required']
  | InferPathParams<Path>['optional']

type OptionalKeys<T> = {
  [K in keyof T]-?: object extends Pick<T, K> ? K : never
}[keyof T]

type InvalidEncodedKeys<Fields extends Schema.Struct.Fields> = {
  [K in keyof Fields]-?: Exclude<Schema.Codec.Encoded<Fields[K]>, undefined> extends string
    ? never
    : K
}[keyof Fields]

type ValidateRouteSchema<
  Path extends string,
  Fields extends Schema.Struct.Fields,
  MissingPathFields = Exclude<AllPathParamNames<Path>, keyof Fields>,
  RequiredPathFieldsMarkedOptional = Extract<
    InferPathParams<Path>['required'],
    OptionalKeys<Schema.Struct.Encoded<Fields>>
  >,
  OptionalPathFieldsMarkedRequired = Exclude<
    InferPathParams<Path>['optional'],
    OptionalKeys<Schema.Struct.Encoded<Fields>>
  >,
  InvalidFields = InvalidEncodedKeys<Fields>
> = [MissingPathFields] extends [never]
  ? [RequiredPathFieldsMarkedOptional] extends [never]
    ? [OptionalPathFieldsMarkedRequired] extends [never]
      ? [InvalidFields] extends [never]
        ? 'name' extends keyof Fields
          ? "Route schemas cannot contain the discriminant field 'name'"
          : [
              Schema.Struct.DecodingServices<Fields> | Schema.Struct.EncodingServices<Fields>
            ] extends [never]
          ? unknown
          : 'Route schemas must be synchronous'
        : `Route fields must encode to strings: ${InvalidFields & string}`
      : `Optional path fields must use Schema.optional: ${OptionalPathFieldsMarkedRequired &
          string}`
    : `Required path fields cannot use Schema.optional: ${RequiredPathFieldsMarkedOptional &
        string}`
  : `Missing route schema fields: ${MissingPathFields & string}`

type ValidatePathWithoutSchema<Path extends string> = [AllPathParamNames<Path>] extends [never]
  ? unknown
  : `Missing route schema fields: ${AllPathParamNames<Path>}`

type RouteParams<Fields extends Schema.Struct.Fields> = Schema.Struct.Type<Fields>

export type Route<Key extends string, Path extends string, Fields extends Schema.Struct.Fields> = ((
  params: RouteParams<Fields>
) => string) & {
  key: Key
  match: MatchFn<Key, Fields>
  paramNames: string[]
  pattern: Path
  stringify: (params: unknown) => string | undefined
}

export type AnyRoute = {
  key: string
  match: (path: string, queryParams?: Record<string, string>) => unknown
  paramNames: string[]
  pattern: string
  stringify: (params: unknown) => string | undefined
}

export type RouteMatch<Name extends string, Fields extends Schema.Struct.Fields> = {
  readonly name: Name
} & RouteParams<Fields>

export type RouteMatchPartial<Name extends string, Fields extends Schema.Struct.Fields> = Partial<
  RouteMatch<Name, Fields>
> & { readonly name: Name }

export type RouteMatchType<R> = R extends Route<infer Name, string, infer Fields>
  ? RouteMatch<Name, Fields>
  : never

type MatchFn<Name extends string, Fields extends Schema.Struct.Fields> = (
  path: string,
  queryParams?: Record<string, string>
) => ({ readonly name: Name } & RouteParams<Fields>) | undefined

type PathToken = string | PathParam

type PathParam = {
  modifier: '' | '*' | '+' | '?'
  name: string
}

const isPathParam = (token: PathToken): token is PathParam => typeof token !== 'string'

/**
 * Pathname tokens identify path fields in the flat schema; all remaining fields
 * are query parameters. Decoding the whole schema decides whether a URL matches,
 * so routes sharing a pathname can be composed from most-specific to fallback.
 */
export function route<Key extends string, Path extends string>(
  key: Key,
  path: Path & ValidatePathWithoutSchema<Path>
): Route<Key, Path, Record<never, never>>
export function route<Key extends string, Path extends string, Fields extends Schema.Struct.Fields>(
  key: Key,
  path: Path,
  schema: Schema.Struct<Fields>,
  ...validation: ValidateRouteSchema<Path, Fields> extends string
    ? [error: ValidateRouteSchema<Path, Fields>]
    : []
): Route<Key, Path, Fields>
export function route(
  key: string,
  path: string,
  schema: Schema.Struct<Schema.Struct.Fields> = Schema.Struct({}),
  ..._validation: unknown[]
): Route<string, string, Schema.Struct.Fields> {
  const pathTokens = parseTokens(path.split('/'))
  const pathParamNames = pathTokens.filter(isPathParam).map((token) => token.name)
  const pathParamNameSet = new Set(pathParamNames)
  const paramNames = Object.keys(schema.fields)
  const queryParamNames = paramNames.filter((name) => !pathParamNameSet.has(name))
  const matchPath = makeRouteMatcher(path)

  // The public generic rejects schemas with dependencies. This erased view lets
  // the owning layer use the synchronous Option decoders without leaking casts
  // or Effect contexts into every route declaration.
  const runtimeSchema = schema as unknown as Schema.Codec<
    Record<string, unknown>,
    Record<string, string | undefined>,
    never,
    never
  >
  const decode = Schema.decodeUnknownOption(runtimeSchema)
  const encode = Schema.encodeUnknownOption(runtimeSchema)

  const stringify = (params: unknown): string | undefined => {
    const encoded = Option.getOrUndefined(encode(params))
    if (!encoded) return undefined

    const pathname = stringifyPath(encoded, pathTokens)
    const query = queryParamNames.reduce<Record<string, string | undefined>>(
      (result, name) => ({ ...result, [name]: encoded[name] }),
      {}
    )
    return addQueryParams(pathname, query)
  }

  const routeFn = ((params: unknown) => {
    const result = stringify(params)
    if (result == undefined) throw new Error(`Invalid params for route: ${key}`)
    return result
  }) as Route<string, string, Schema.Struct.Fields>

  routeFn.key = key
  routeFn.match = (pathname: string, queryParams?: Record<string, string>) => {
    const [matches, pathParams] = matchPath(pathname)
    if (!matches) return undefined

    const raw = paramNames.reduce<Record<string, string>>((result, name) => {
      const value = pathParamNameSet.has(name) ? pathParams[name] : queryParams?.[name]
      return value == undefined ? result : { ...result, [name]: value }
    }, {})
    const decoded = Option.getOrUndefined(decode(raw))
    if (!decoded) return undefined

    return { ...decoded, name: key }
  }
  routeFn.paramNames = paramNames
  routeFn.pattern = path
  routeFn.stringify = stringify

  return routeFn
}

function parseTokens(path: string[]): PathToken[] {
  return path.reduce<PathToken[]>((tokens, fragment) => {
    if (!fragment) return tokens
    if (!fragment.startsWith(':')) return tokens.concat(fragment)

    const maybeModifier = fragment[fragment.length - 1]
    const modifier =
      maybeModifier === '+' || maybeModifier === '*' || maybeModifier === '?' ? maybeModifier : ''
    return tokens.concat({
      modifier,
      name: fragment.slice(1, modifier ? fragment.length - 1 : undefined)
    })
  }, [])
}

function stringifyPath(
  encoded: Record<string, string | undefined>,
  pathTokens: PathToken[]
): string {
  const segments = pathTokens.reduce<string[]>((result, token) => {
    if (!isPathParam(token)) return result.concat(token)

    const value = encoded[token.name]
    if (value == undefined) return result

    if (token.modifier == '*' || token.modifier == '+') return result.concat(value)
    return result.concat(encodeURIComponent(value))
  }, [])
  const pathname = ['', ...segments].join('/')
  return pathname.length == 0 ? '/' : pathname
}
